import {
  AfterViewInit,
  Component,
  ElementRef,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
  computed,
  effect,
  input,
  output,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TRANSLOCO_SCOPE, TranslocoModule } from '@jsverse/transloco';

import { QaiButtonComponent, QaiDialogComponent, QaiLoadingComponent } from '@qai/ui';

import { AuthorService } from 'src/app/services/self-serve/author.service';
import { IGeneratedCallDetails } from 'src/app/interfaces/self-serve/responses/igenerated-call-details';
import { GeneratedCallDetailsComponent } from 'src/app/pages/self-serve/simulation-builder/components/generated-call-details/generated-call-details.component';
import { AmplitudeService } from 'src/app/services/external-tools/amplitude.service';
import { AmplitudeTrackingEvents } from 'src/app/utils/enums/amplitude/amplitudeTrackingEvents.enum';
import { SelfAuthoringPersonaFormService } from 'src/app/pages/self-serve/simulation-builder/services/self-authoring-persona-form.service';
import { CallRefinementPollingService } from 'src/app/services/self-serve/call-refinement-polling.service';
import { CallRefinementStatus } from 'src/app/utils/enums/self-serve/call-refinement-status.enum';
import { SimulationGenerationPollingService } from 'src/app/services/self-serve/simulation-generation-polling.service';

@Component({
  selector: 'generated-simulation-details-modal',
  standalone: true,
  templateUrl: './generated-simulation-details-modal.component.html',
  styleUrls: ['./generated-simulation-details-modal.component.scss'],
  imports: [
    CommonModule,
    QaiButtonComponent,
    QaiDialogComponent,
    QaiLoadingComponent,
    GeneratedCallDetailsComponent,
    TranslocoModule,
  ],
  providers: [{ provide: TRANSLOCO_SCOPE, useValue: 'simulationBuilder' }],
})
export class GeneratedSimulationDetailsModalComponent
  implements OnChanges, AfterViewInit, OnDestroy
{
  readonly open = input(false);
  readonly callUuid = input<string>('');
  /** Carried through to the completed/failed-refinement toast's "View
   * simulation" link — SimulationDetailsComponent's fetch needs this as a
   * query param, or the simulation details page fails to load. */
  readonly moduleUuid = input<string>('');
  /** When true, a QC Admin has manually overridden the converse prompt.
   * Passed through to GeneratedCallDetailsComponent to permanently block
   * the author's refinement input. */
  readonly isRefinementBlocked = input(false);

  readonly selectAvatarClicked = output<void>();
  readonly requestChangesSubmitted = output<string>();
  readonly closed = output<void>();

  @ViewChild('dialog') dialogRef?: QaiDialogComponent;
  @ViewChild('dialog', { read: ElementRef }) dialogElementRef?: ElementRef;

  details = signal<IGeneratedCallDetails | null>(null);
  loading = signal(false);
  loadError = signal(false);
  contentMaxHeight = signal('');

  private resizeObserver?: ResizeObserver;

  // Mirrors the height-budget approach in preview-simulation-modal.component.ts —
  // the dialog itself is capped (see .scss), and this works out how much of
  // that budget is left for the scrollable text content after the (variable-
  // height) heading, and the fixed-height divider/prompt-row/footer chrome.
  private readonly MODAL_HEIGHT_CONFIG = {
    totalModalHeightVh: 75,
    innerPaddingVh: 4,
    headerMarginBottomVh: 2,
    promptRowAndFooterVh: 16,
    minContentHeightVh: 20,
    defaultHeadingHeightPx: 60,
  };

  /** Raw polling result — drives the refresh trigger below. Never read this
   * from the template; use refinementStatus(), which additionally holds at
   * Thinking while the post-completion content refresh is in flight. */
  private readonly pollingStatus = computed(() =>
    this.callRefinementPollingService.getStatus(this.callUuid()),
  );
  readonly isContentRefreshing = signal(false);

  readonly refinementStatus = computed(() => {
    const status = this.pollingStatus();
    if (status === CallRefinementStatus.Updated && this.isContentRefreshing()) {
      return CallRefinementStatus.Thinking;
    }
    return status;
  });

  /** Set to true once the user submits a refinement request this session —
   * guards the Amplitude outcome event so it only fires once per submission,
   * not on re-open when a previous terminal status is still in the record. */
  private refinementSubmittedThisSession = false;

  constructor(
    private authorService: AuthorService,
    private amplitudeService: AmplitudeService,
    private selfAuthoringPersonaFormService: SelfAuthoringPersonaFormService,
    private callRefinementPollingService: CallRefinementPollingService,
    private simulationGenerationPollingService: SimulationGenerationPollingService,
  ) {
    effect(() => {
      // React to the raw polling status, not the gated refinementStatus() —
      // that one holds at Thinking *because* isContentRefreshing is true,
      // so reacting to it here would re-trigger a refresh every time it
      // flips back to Updated once the fetch below completes.
      if (this.pollingStatus() === CallRefinementStatus.Updated) {
        this.refreshCallDetailsInPlace();
      }
    });

    effect(() => {
      // Always read reactive signals first so Angular tracks them as dependencies
      // and re-runs the effect when they change — even if the guard below exits early.
      const status = this.pollingStatus();
      const callUuid = this.callUuid();
      if (!this.refinementSubmittedThisSession) return;
      if (status === CallRefinementStatus.Updated) {
        this.amplitudeService.trackEvent(
          AmplitudeTrackingEvents.SELF_AUTHORING_REFINEMENT_REQUEST_SUCCEEDED,
          { call_uuid: callUuid },
        );
        this.refinementSubmittedThisSession = false;
      } else if (status === CallRefinementStatus.Error) {
        this.amplitudeService.trackEvent(
          AmplitudeTrackingEvents.SELF_AUTHORING_REFINEMENT_REQUEST_FAILED,
          { call_uuid: callUuid, failure_reason: 'processing_error' },
        );
        this.refinementSubmittedThisSession = false;
      } else if (status === CallRefinementStatus.OutOfScope) {
        this.amplitudeService.trackEvent(
          AmplitudeTrackingEvents.SELF_AUTHORING_REFINEMENT_REQUEST_FAILED,
          { call_uuid: callUuid, failure_reason: 'out_of_scope' },
        );
        this.refinementSubmittedThisSession = false;
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['open']) {
      if (this.open()) {
        console.log('generated-simulation-details-modal inputs:', {
          open: this.open(),
          callUuid: this.callUuid(),
        });
        this.amplitudeService.trackEvent(
          AmplitudeTrackingEvents.SELF_AUTHORING_CALL_DETAILS_VIEWED,
          { call_uuid: this.callUuid() }
        );
        this.dialogRef?.showModal();
        this.fetchCallDetails();
      } else {
        this.dialogRef?.close();
        // This component is always mounted (see its template usage — no
        // @if around it), so ngOnDestroy never fires just from closing the
        // dialog. Clear a finished refinement now so it doesn't resurface
        // stale on the next open; a still-running one is left alone (see
        // dismissRefinementIfNotInFlight).
        this.dismissRefinementIfNotInFlight();
      }
    }
  }

  ngAfterViewInit(): void {
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.calculateContentHeight());
      if (this.dialogElementRef?.nativeElement) {
        this.resizeObserver.observe(this.dialogElementRef.nativeElement);
      }
    }
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.dismissRefinementIfNotInFlight();
  }

  /** "Updated"/"Error" are done — clear them so the next time this callUuid
   * is viewed (dialog reopened, or component recreated) it starts clean. A
   * still-running "Thinking" refinement is left untouched: it must keep
   * polling in the background after this view closes (see
   * CallRefinementPollingService class doc). */
  private dismissRefinementIfNotInFlight(): void {
    const callUuid = this.callUuid();
    if (callUuid && this.refinementStatus() !== CallRefinementStatus.Thinking) {
      this.callRefinementPollingService.dismiss(callUuid);
    }
  }

  private fetchCallDetails(): void {
    const callUuid = this.callUuid();
    if (!callUuid) return;

    this.loading.set(true);
    this.loadError.set(false);
    this.details.set(null);

    this.authorService.getGeneratedCallDetails(callUuid).subscribe({
      next: (response) => {
        this.details.set(response);
        this.loading.set(false);
        this.selfAuthoringPersonaFormService.saveGeneratedDetails(callUuid, {
          avatarName: response.avatarName,
          avatarGender: response.avatarGender,
          welcomeMessage: response.welcomeMessage,
          resumeMessage: response.resumeMessage,
        });
        setTimeout(() => this.calculateContentHeight());
      },
      error: (err) => {
        console.error('Failed to fetch generated call details:', err);
        this.loadError.set(true);
        this.loading.set(false);
      },
    });
  }

  /** Swaps in the refreshed content after a refinement completes. Shows the
   * per-section skeleton (isContentRefreshing) instead of the full-view
   * loader/blank state while the fetch is in flight — the old content stays
   * visible underneath conceptually, since the skeleton replaces only the
   * content area, not the whole card. */
  private refreshCallDetailsInPlace(): void {
    const callUuid = this.callUuid();
    if (!callUuid) return;

    this.isContentRefreshing.set(true);

    this.authorService.getGeneratedCallDetails(callUuid).subscribe({
      next: (response) => {
        this.details.set(response);
        this.selfAuthoringPersonaFormService.saveGeneratedDetails(callUuid, {
          avatarName: response.avatarName,
          avatarGender: response.avatarGender,
          welcomeMessage: response.welcomeMessage,
          resumeMessage: response.resumeMessage,
        });
        this.isContentRefreshing.set(false);
        setTimeout(() => this.calculateContentHeight());
      },
      error: (err) => {
        console.error('Failed to refresh call details after refinement:', err);
        // Keep showing the previous content rather than blanking the view.
        this.isContentRefreshing.set(false);
      },
    });
  }

  private calculateContentHeight(): void {
    const dialogElement = this.dialogElementRef?.nativeElement;
    if (!dialogElement) return;

    const title = this.details()?.title?.trim();
    let headingElement: Element | null = null;

    if (title) {
      const allElements = dialogElement.querySelectorAll('*');
      for (const element of allElements) {
        if (element.textContent?.trim() === title) {
          headingElement = element;
          break;
        }
      }
    }

    const headingHeightPx = headingElement
      ? (headingElement as HTMLElement).offsetHeight
      : this.MODAL_HEIGHT_CONFIG.defaultHeadingHeightPx;

    const pixelsIn1Vh = window.innerHeight / 100;
    const headingHeightVh = Math.ceil(headingHeightPx / pixelsIn1Vh);

    const totalFixedHeightVh =
      this.MODAL_HEIGHT_CONFIG.innerPaddingVh +
      this.MODAL_HEIGHT_CONFIG.headerMarginBottomVh +
      this.MODAL_HEIGHT_CONFIG.promptRowAndFooterVh;

    const availableHeightVh =
      this.MODAL_HEIGHT_CONFIG.totalModalHeightVh - headingHeightVh - totalFixedHeightVh;

    this.contentMaxHeight.set(
      `${Math.max(availableHeightVh, this.MODAL_HEIGHT_CONFIG.minContentHeightVh)}vh`
    );
  }

  handleSelectAvatarClick(): void {
    this.selectAvatarClicked.emit();
  }

  handleRequestChangesSubmitted(value: string): void {
    this.requestChangesSubmitted.emit(value);

    const callUuid = this.callUuid();
    if (!callUuid) return;

    this.amplitudeService.trackEvent(
      AmplitudeTrackingEvents.SELF_AUTHORING_FEEDBACK_SUBMITTED,
      { call_uuid: callUuid, feedback_text: value }
    );

    const callName = this.details()?.title;
    const moduleUuid = this.moduleUuid();
    const generationUuid = this.simulationGenerationPollingService.getGeneration(callUuid)?.generationUuid;
    this.authorService.requestCallRefinement(callUuid, value).subscribe({
      next: () => {
        this.refinementSubmittedThisSession = true;
        this.callRefinementPollingService.start(callUuid, callName, moduleUuid, undefined, undefined, generationUuid);
      },
      error: (err) => {
        console.error('Failed to request call refinement:', err);
        // The input was disabled optimistically on submit — since no polling
        // record exists to ever flip refinementStatus away from Idle, mark
        // it failed directly so the UI unlocks and shows the error.
        // Set the guard before fail() so the outcome effect fires for this
        // visible failure path too.
        this.refinementSubmittedThisSession = true;
        this.callRefinementPollingService.fail(callUuid, callName, moduleUuid);
      },
    });
  }

  handleDismissRefinementError(): void {
    this.callRefinementPollingService.dismiss(this.callUuid());
  }

  handleAfterClose(): void {
    this.closed.emit();
  }
}
